import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PurchaseOrder } from "@models/PurchaseOrder";
import { ShippingParameter } from "@models/ShippingParameter";
import { ShippingParameterVariant } from "@models/ShippingParameterVariant";
import client, { useMutateError } from "@services/api";
import { kebabCaseKeysDeep } from "@utility/utilityFunctions";

import { purchaseOrdersKeyFactory } from "./purchaseOrderQueries";

export const shippingParametersKeyFactory = createQueryKeys(
  "shippingParameters",
  {
    list: (purchaseOrderId: string) => ({
      queryKey: [purchaseOrderId],
      queryFn: () =>
        client
          .get<ShippingParameter[]>(`/shipping-parameters`, {
            params: {
              filter: { purchaseOrderId },
            },
          })
          .then((res) => res.data),
    }),
  }
);

export const useShippingParametersListQuery = (
  purchaseOrderId?: string | null
) => {
  return useQuery({
    ...shippingParametersKeyFactory.list(purchaseOrderId!),
    enabled: !!purchaseOrderId,
  });
};

type UpdateShippingParameterPayload = {
  id: string;
  purchaseOrderId: string;
  addressId?: number;
};

export const useUpdateShippingParameterMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateShippingParameterPayload) =>
      client
        .patch<ShippingParameter>(`/shipping-parameters/${id}`, {
          __type: "shipping-parameter",
          ...payload,
        })
        .then((res) => res.data),
    onError: (error) => setError(error),
    onSuccess: (data, { purchaseOrderId }) => {
      queryClient.setQueryData<ShippingParameter[]>(
        shippingParametersKeyFactory.list(purchaseOrderId).queryKey,
        (old) => old && old.map((sp) => (sp.id === data.id ? data : sp))
      );
      queryClient.invalidateQueries({
        queryKey: purchaseOrdersKeyFactory.detail(purchaseOrderId).queryKey,
      });
    },
  });
};

type ShippingParameterVariantExtra = {
  id: string;
  carrier: string;
  method: string;
  actualShipDate: string;
  shippedQty: number;
  packageCount: number;
  trackingNumber: string;
  tax: string;
};

type UpdateShippingParameterExtrasPayload = {
  purchaseOrderId: string;
  shippingParameterVariants: ShippingParameterVariantExtra[];
};

export const useUpdateShippingParameterExtrasMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      shippingParameterVariants,
    }: UpdateShippingParameterExtrasPayload) =>
      client.patch<ShippingParameter[]>(
        `/shipping-parameter-variants/add-shipping-extras`,
        kebabCaseKeysDeep({
          shippingParameterVariants,
        }),
        { serializeBody: false }
      ),
    onError: (error) => setError(error),
    onSuccess: (_, { purchaseOrderId }) => {
      return queryClient.invalidateQueries({
        queryKey: shippingParametersKeyFactory.list(purchaseOrderId).queryKey,
      });
    },
  });
};

type CancelDestroy =
  | {
      action: "cancel";
      cancelationNote: string;
    }
  | {
      action: "destroy";
    };

type CancelOrDestroyShippingParameterVariantPayload = {
  id: string;
  purchaseOrderId: string;
} & CancelDestroy;

export const useCancelOrDestroyShippingParameterVariantMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      action,
      ...payload
    }: CancelOrDestroyShippingParameterVariantPayload) =>
      client
        .post<ShippingParameterVariant>(
          `/shipping-parameter-variants/${id}/${action}`,
          kebabCaseKeysDeep({
            cancelationType: "order",
            ...payload,
          }),
          { serializeBody: false }
        )
        .then((res) => res.data),
    onError: (error) => setError(error),
    onSuccess: (destroyedSPV, { purchaseOrderId }) => {
      queryClient.setQueryData<PurchaseOrder>(
        purchaseOrdersKeyFactory.detail(purchaseOrderId).queryKey,
        (po) =>
          po && {
            ...po,
            totalTaxes: String(+po.totalTaxes - +destroyedSPV.tax!),
          }
      );
      return queryClient.invalidateQueries({
        queryKey: shippingParametersKeyFactory.list(purchaseOrderId).queryKey,
      });
    },
  });
};
